import { useEffect, useState } from 'react';
import { Plus, RefreshCw, ExternalLink, Building2, ChevronRight, Save, Copy, Mail, Check } from 'lucide-react';
import { useNavigate } from 'react-router';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Checkbox, StatusLine,
} from '../components/ui';

type Club = {
  id: string;
  name: string;
  address: string | null;
  is_active: boolean;
  accepts_freelance_coaches: boolean;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
  created_at: string;
};

export default function Clubs() {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnboard, setShowOnboard] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('clubs').select('*').order('created_at', { ascending: false });
    setClubs((data as Club[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Clubs & courts"
        subtitle="Onboard clubs and manage their Stripe Connect status."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>
            <Button icon={Plus} onClick={() => setShowOnboard(true)}>Onboard club</Button>
          </div>
        }
      />

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading clubs…</div>
        ) : clubs.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No clubs yet"
            description="Onboard your first partner club to start taking court bookings."
            action={<Button icon={Plus} onClick={() => setShowOnboard(true)}>Onboard a club</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Club</th>
                <th className="text-left px-5 py-3 font-semibold">Address</th>
                <th className="text-left px-5 py-3 font-semibold">Stripe</th>
                <th className="text-left px-5 py-3 font-semibold">Freelancers</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {clubs.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                  onClick={() => nav(`/clubs/${c.id}`)}
                >
                  <td className="px-5 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-5 py-3 text-gray-600">{c.address || '—'}</td>
                  <td className="px-5 py-3">
                    {c.stripe_onboarding_completed_at
                      ? <Badge tone="green">Connected</Badge>
                      : c.stripe_account_id
                        ? <Badge tone="amber">KYC pending</Badge>
                        : <Badge tone="gray">Not started</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    {c.accepts_freelance_coaches
                      ? <Badge tone="lime">Open</Badge>
                      : <Badge tone="gray">Staff only</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    {c.is_active
                      ? <Badge tone="green">Live</Badge>
                      : <Badge tone="gray">Not live</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {showOnboard && (
        <OnboardClubModal
          onClose={() => setShowOnboard(false)}
          onCreated={() => { setShowOnboard(false); load(); }}
        />
      )}
    </div>
  );
}

function OnboardClubModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptFreelance, setAcceptFreelance] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a successful create+link, show the link in-modal so admin can
  // hand it to the club owner (copy / mailto) instead of opening
  // Stripe themselves and ending up doing the KYC under the wrong
  // identity. The owner clicks from their inbox.
  const [readyLink, setReadyLink] = useState<{
    url: string; clubName: string; ownerEmail: string | null;
  } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const { data: club, error: insertErr } = await supabase
      .from('clubs')
      .insert([{
        name,
        address: address || null,
        contact_email: email || null,
        contact_phone: phone || null,
        accepts_freelance_coaches: acceptFreelance,
        is_active: false,
      }])
      .select('id, name, contact_email').single();

    if (insertErr || !club) {
      setBusy(false); setError(insertErr?.message || 'Could not create club'); return;
    }
    const { data, error: linkErr } = await supabase.functions.invoke('stripe-connect-onboarding', {
      body: { clubId: club.id },
    });
    setBusy(false);
    if (linkErr || !data?.url) {
      setError((linkErr as any)?.message || 'Could not create Stripe onboarding link'); return;
    }
    setReadyLink({ url: data.url, clubName: club.name, ownerEmail: club.contact_email ?? null });
  };

  if (readyLink) {
    return (
      <Modal
        title="Stripe onboarding link ready"
        subtitle="Send this link to the club owner — they finish KYC themselves."
        onClose={() => { onCreated(); }}
      >
        <StripeLinkPanel
          url={readyLink.url}
          clubName={readyLink.clubName}
          ownerEmail={readyLink.ownerEmail}
          onDone={() => onCreated()}
        />
      </Modal>
    );
  }

  return (
    <Modal
      title="Onboard a new club"
      subtitle="Creates the club record and a Stripe Connect link the owner finishes from their inbox."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Club name">
          <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Pro Tennis Academy" />
        </Field>
        <Field label="Address">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Al Quoz, Dubai" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner / contact email" hint="The Stripe link is sent to this address.">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Contact phone">
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        <Checkbox
          checked={acceptFreelance}
          onChange={setAcceptFreelance}
          label="Accept freelance coaches at this club"
        />

        {error && <StatusLine kind="error">{error}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {busy ? 'Creating…' : 'Create & generate link'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Reusable panel that surfaces a Stripe Connect onboarding link with
 * three "hand to owner" affordances:
 *   - Copy link to clipboard
 *   - Open the user's mail client with a prefilled draft addressed
 *     to the club owner
 *   - Open Stripe directly (escape hatch for when admin happens to
 *     be the owner too, e.g. during an in-person sign-up)
 *
 * Lives in apps/admin/src/pages/Clubs.tsx today; lift to a shared
 * component if a third call site appears.
 */
export function StripeLinkPanel({
  url, clubName, ownerEmail, onDone,
}: {
  url: string;
  clubName: string;
  ownerEmail: string | null;
  onDone?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked — admin can still select + copy from the
      // text field below.
    }
  };

  const mailtoHref = (() => {
    const subject = encodeURIComponent(`Finish setting up ${clubName} on AceRent`);
    const body = encodeURIComponent(
      `Hi,\n\n`
      + `Your AceRent club "${clubName}" is set up. The last step is finishing Stripe `
      + `Connect KYC so we can pay out your bookings.\n\n`
      + `It's a one-time form (~5 minutes) hosted by Stripe:\n\n`
      + `${url}\n\n`
      + `Once you're done, your club goes live in customer search automatically.\n\n`
      + `Thanks,\nThe AceRent team\n`,
    );
    const to = ownerEmail ? encodeURIComponent(ownerEmail) : '';
    return `mailto:${to}?subject=${subject}&body=${body}`;
  })();

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
          Onboarding link
        </div>
        <input
          readOnly
          value={url}
          onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
          className="w-full text-xs font-mono bg-white border border-gray-200 rounded-md px-2 py-1.5"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button
          type="button"
          variant="secondary"
          icon={copied ? Check : Copy}
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <a
          href={mailtoHref}
          className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-lg border border-gray-200 hover:border-gray-400 text-sm font-medium text-gray-700"
        >
          <Mail className="w-4 h-4" />
          {ownerEmail ? 'Email owner' : 'Compose email'}
        </a>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-lg border border-gray-200 hover:border-gray-400 text-sm font-medium text-gray-700"
        >
          <ExternalLink className="w-4 h-4" />
          Open in tab
        </a>
      </div>

      {ownerEmail && (
        <div className="text-xs text-gray-500">
          Drafts the email pre-filled to <span className="font-mono">{ownerEmail}</span> in
          your default mail client.
        </div>
      )}
      {!ownerEmail && (
        <StatusLine kind="error">
          No contact email on file — set one on the club's Details page so the email
          shortcut can prefill the recipient.
        </StatusLine>
      )}

      {onDone && (
        <div className="flex justify-end pt-1">
          <Button type="button" onClick={onDone}>Done</Button>
        </div>
      )}

      <div className="text-[11px] text-gray-500 leading-relaxed">
        The link expires after a few hours. If it goes stale, regenerate it from the
        club detail page.
      </div>
    </div>
  );
}
